import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { GetSchoolById } from 'src/shared/store/actions/schools.action';

import { ISchool } from '../../interfaces/groups/school.interface';
import { SchoolsService } from '../../services/schools.service';
import { SetSelectedSchool } from '../actions/auth.action';
import {
    CreateNewSchool,
    DeleteSchool,
    DeleteSchoolToEdit,
    DisableSchool,
    EnableSchool,
    GetAllSchools,
    PatchSchool,
    ResetSchools,
    SetSchoolToEdit,
} from '../actions/schools.action';

export interface SchoolsStateModel {
    schools: ISchool[];
    schoolToEdit: ISchool;
}

@State<SchoolsStateModel>({
    name: 'schools',
    defaults: {
        schools: [],
        schoolToEdit: null,
    },
})
@Injectable()
export class SchoolsState {
    constructor(private schoolsService: SchoolsService) {}
    @Selector()
    public static getAllSchools(state: SchoolsStateModel): ISchool[] {
        return state.schools;
    }

    @Selector()
    public static schoolToEdit(state: SchoolsStateModel): ISchool {
        return state.schoolToEdit;
    }

    @Action(GetAllSchools)
    public getAllSchools(ctx: StateContext<SchoolsStateModel>): Observable<ISchool[]> {
        return this.schoolsService.getAllSchools().pipe(
            tap((result: ISchool[]) => {
                if (result) {
                    ctx.patchState({
                        schools: result,
                    });
                }
            })
        );
    }

    @Action(SetSchoolToEdit)
    public setSchoolToEdit(ctx: StateContext<SchoolsStateModel>, action: SetSchoolToEdit): Observable<ISchool> | void {
        const state: SchoolsStateModel = ctx.getState();
        const schoolFound: ISchool = state.schools.find((school: ISchool) => school.id === action.id);
        if ((!state.schools.length && !schoolFound) || action.forceCall) {
            return this.schoolsService.getSchoolById(action.id).pipe(
                tap((result: ISchool) => {
                    if (result) {
                        ctx.patchState({
                            schoolToEdit: result,
                        });
                    }
                })
            );
        } else {
            ctx.patchState({
                schoolToEdit: schoolFound,
            });
        }
    }

    @Action(GetSchoolById)
    public getSchoolById(ctx: StateContext<SchoolsStateModel>, action: SetSchoolToEdit): Observable<ISchool> | void {
        const state: SchoolsStateModel = ctx.getState();
        return this.schoolsService.getSchoolById(action.id).pipe(
            tap((result: ISchool) => {
                if (result) {
                    ctx.patchState({
                        schoolToEdit: result,
                    });
                    ctx.dispatch(new SetSelectedSchool(result));
                    if (state.schools.length) {
                        ctx.setState(
                            patch({
                                schools: updateItem<ISchool>((entity: ISchool) => entity.id === result.id, result),
                            })
                        );
                    }
                }
            })
        );
    }

    // CREATE
    @Action(CreateNewSchool)
    public createNewSchool(ctx: StateContext<SchoolsStateModel>, action: CreateNewSchool): Observable<ISchool> {
        return this.schoolsService.createSchool(action.newSchool).pipe(
            tap((result: ISchool) => {
                ctx.setState(
                    patch({
                        schoolToEdit: null,
                        schools: append([result]),
                    })
                );
            })
        );
    }

    // UPDATE
    @Action(PatchSchool)
    public updateSchool(ctx: StateContext<SchoolsStateModel>, action: PatchSchool): Observable<ISchool> {
        const schoolIdToUpdate: number = action.school.school.id;
        return this.schoolsService.patchSchool(action.school, schoolIdToUpdate).pipe(
            tap((result: ISchool) => {
                ctx.setState(
                    patch({
                        schools: updateItem<ISchool>((entity: ISchool) => entity.id === schoolIdToUpdate, result),
                    })
                );
            })
        );
    }

    // DELETE
    @Action(DeleteSchool)
    public deleteSchool(ctx: StateContext<SchoolsStateModel>, action: DeleteSchool): Observable<void> {
        return this.schoolsService.deleteSchool(action.schoolId).pipe(
            tap(() => {
                ctx.patchState({
                    schools: ctx.getState().schools.filter((school: ISchool) => school.id !== action.schoolId),
                });
            })
        );
    }

    // DELETE -> ENABLE & DISABLE
    @Action(EnableSchool)
    public enableSchool(ctx: StateContext<SchoolsStateModel>, action: EnableSchool): Observable<ISchool> {
        return this.schoolsService.enableSchool(action.schoolId).pipe(
            tap((result: ISchool) => {
                ctx.setState(
                    patch({
                        schools: updateItem<ISchool>((school: ISchool) => school.id === result.id, result),
                    })
                );
            })
        );
    }

    @Action(DisableSchool)
    public disableSchool(ctx: StateContext<SchoolsStateModel>, action: DisableSchool): Observable<ISchool> {
        return this.schoolsService.disableSchool(action.schoolId).pipe(
            tap((result: ISchool) => {
                ctx.setState(
                    patch({
                        schools: updateItem<ISchool>((school: ISchool) => school.id === result.id, result),
                    })
                );
            })
        );
    }

    // UTILS
    @Action(DeleteSchoolToEdit)
    public deleteSchoolToEdit(ctx: StateContext<SchoolsStateModel>): void {
        ctx.patchState({
            schoolToEdit: null,
        });
    }

    @Action(ResetSchools)
    public resetSchools(ctx: StateContext<SchoolsStateModel>): void {
        ctx.patchState({
            schools: [],
            schoolToEdit: null,
        });
    }
}
